// @flow
import { Formik } from 'formik';
import React from 'react';
import { object } from 'yup';
import { useHistory, useParams } from 'react-router-dom';
import { Grid } from 'semantic-ui-react';
import {
  useAddTableFlowResourceMutation,
  useGetOrgAvailableClusterResourcesDetailsQuery,
  useUpdateTableFlowResourceMutation,
} from '@streaming-projects/service-definitions/streamingProjectsApi';
import { getValidationSchemaFromColsConfig } from '@src/configuration/utils';
import { StyledContainer } from '@src/common-utils/styledComponents';
import { ConfirmModal } from '@presentational/modals/ConfirmModal';
import { Spacer } from '@presentational/spacing/Spacer';
import { toastError } from '@presentational/notifications/utils';
import { getLinkForOrganizationsPage } from '@streaming-projects/links';
import { ResourceEditRepriceWarningContainer } from '@streaming-projects/resource-definitions/auxilary-components/ResourceEditRepriceWarningContainer';
import {
  TABLEFLOW_RESOURCES_GRID_FIELDNAMES,
  TABLEFLOW_RESOURCES_GRID_HEADERS,
} from '@streaming-projects/orgs/enums';
import { QueryError } from '@presentational/QueryError';
import { AlertModal } from '@presentational/modals/AlertModal';

import { SPTableFlowResourceNameContainer } from './SPTableFlowResourceNameContainer';
import { SPTableFlowClusterResourceNameContainer } from './SPTableFlowClusterResourceNameContainer';
import { SPTableFlowStorageTypeContainer } from './SPTableFlowStorageTypeContainer';
import { TABLEFLOW_RESOURCE_INPUTS_DETAILS_CONFIG } from './tableflow-resource-inputs-config';

const getAddAndUpdateParams = (orgId, initialValues, addTableFlowFormik) => {
  // Note, tableFlowResourceId and Version are only applicable in Editing Mode and hence the usage of "?."
  return {
    orgId,
    tableFlowResourceId: initialValues?.[TABLEFLOW_RESOURCES_GRID_HEADERS.ID],
    payload: {
      org_id: orgId,
      name: addTableFlowFormik.values[TABLEFLOW_RESOURCES_GRID_FIELDNAMES.NAME],
      cluster_resource_id:
        addTableFlowFormik.values[TABLEFLOW_RESOURCES_GRID_FIELDNAMES.CLUSTER_RESOURCE_ID],
      storage_type: addTableFlowFormik.values[TABLEFLOW_RESOURCES_GRID_FIELDNAMES.STORAGE_TYPE],
      version: initialValues?.[TABLEFLOW_RESOURCES_GRID_HEADERS.VERSION],
    },
  };
};

export const SPTableFlowConfigurationAddEditModal = ({
  isOpen,
  setOpen,
  isEditingMode = false,
  initialValues = null,
}) => {
  const { orgId } = useParams();
  const [addTableFlowResource] = useAddTableFlowResourceMutation();
  const [updateTableFlowResource] = useUpdateTableFlowResourceMutation();
  const { push } = useHistory();

  /*
        1. For both Editing Mode and Add Mode, we have to fetch the latest cluster resources.
        2. Post that, we let user choose from the list in the Add Mode or update the existing ones in Editing Mode
   */

  // Note: We cannot use DataFetcherComponent here as we need more control
  const { currentData, error, isLoading, isFetching } =
    useGetOrgAvailableClusterResourcesDetailsQuery(
      { orgId },
      {
        refetchOnMountOrArgChange: true,
        skip: !orgId || !isOpen,
      }
    );

  if (isLoading || isFetching || !isOpen) {
    return null;
  }

  if (error) {
    return <QueryError error={error} />;
  }

  const availableClusterResources = currentData?.resources?.cluster_resources ?? [];
  // Note, ideally, in editing mode, this (availableClusterResources.length === 0) should never happen
  // In an extreme edge case where someone has deleted the cluster resources and the tableflow resources, this will show incorrect message, but that's okay
  if (availableClusterResources.length === 0) {
    return (
      <StyledContainer>
        <AlertModal
          body="No cluster resource found. Please configure a cluster resource to create Tableflow resources!"
          header="Add New Tableflow Resource Configuration"
          isOpen={isOpen}
          onClose={() => {
            return setOpen(false);
          }}
        />
      </StyledContainer>
    );
  }

  const initialValuesToUse = isEditingMode
    ? {
        [TABLEFLOW_RESOURCES_GRID_FIELDNAMES.NAME]:
          initialValues[TABLEFLOW_RESOURCES_GRID_HEADERS.NAME],
        [TABLEFLOW_RESOURCES_GRID_FIELDNAMES.CLUSTER_RESOURCE_ID]:
          initialValues[TABLEFLOW_RESOURCES_GRID_HEADERS.CLUSTER_RESOURCE_ID],
        [TABLEFLOW_RESOURCES_GRID_FIELDNAMES.STORAGE_TYPE]:
          initialValues[TABLEFLOW_RESOURCES_GRID_HEADERS.STORAGE_TYPE],
      }
    : {
        [TABLEFLOW_RESOURCES_GRID_FIELDNAMES.NAME]: '',
        [TABLEFLOW_RESOURCES_GRID_FIELDNAMES.CLUSTER_RESOURCE_ID]: '',
        [TABLEFLOW_RESOURCES_GRID_FIELDNAMES.STORAGE_TYPE]: '',
      };
  const validationSchema = object({
    ...getValidationSchemaFromColsConfig(TABLEFLOW_RESOURCE_INPUTS_DETAILS_CONFIG),
  });

  return (
    <StyledContainer>
      <Formik
        autoComplete="off"
        enableReinitialize={true}
        initialValues={initialValuesToUse}
        onSubmit={() => {}}
        validateOnMount={true}
        validationSchema={validationSchema}
      >
        {(addTableFlowFormik) => {
          return (
            <ConfirmModal
              body={
                <>
                  <Grid columns={1}>
                    <Grid.Row>
                      <Grid.Column width={16}>
                        <SPTableFlowResourceNameContainer
                          disableOnFormErrors={false}
                          disabled={false}
                          fluid={true}
                        />
                      </Grid.Column>
                      <Grid.Column width={16}>
                        <SPTableFlowClusterResourceNameContainer
                          availableClusterResources={availableClusterResources}
                          disableOnFormErrors={false}
                          disabled={false}
                          fluid={true}
                        />
                      </Grid.Column>
                      <Grid.Column width={16}>
                        <SPTableFlowStorageTypeContainer
                          disableOnFormErrors={false}
                          disabled={false}
                          fluid={true}
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                  <Spacer y={20} />
                  {isEditingMode && (
                    <ResourceEditRepriceWarningContainer resourceType="Tableflow" />
                  )}
                </>
              }
              cancelButtonNegative={true}
              disabled={!addTableFlowFormik.isValid || !addTableFlowFormik.dirty}
              header={
                isEditingMode
                  ? 'Edit Tableflow Resource Configuration'
                  : 'Add New Tableflow Resource Configuration'
              }
              isOpen={isOpen}
              okButtonNegative={false}
              okButtonText="Continue"
              onClickHandlerForCancel={() => {
                addTableFlowFormik.resetForm();
                return setOpen(false);
              }}
              onClickHandlerForOK={async () => {
                let errorInCreateOrUpdate;

                if (!isEditingMode) {
                  const { error } = await addTableFlowResource(
                    getAddAndUpdateParams(orgId, initialValues, addTableFlowFormik)
                  );
                  errorInCreateOrUpdate = error;
                } else {
                  const { error } = await updateTableFlowResource(
                    getAddAndUpdateParams(orgId, initialValues, addTableFlowFormik)
                  );
                  errorInCreateOrUpdate = error;
                }

                if (errorInCreateOrUpdate) {
                  toastError(errorInCreateOrUpdate);
                } else {
                  push(getLinkForOrganizationsPage(orgId));
                }
                addTableFlowFormik.resetForm();
                setOpen(false);
              }}
            />
          );
        }}
      </Formik>
    </StyledContainer>
  );
};
